import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ChildActivationEnd, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { paths } from '@platform/paths';
import { AppVersionService } from '@shared/services/app-version.service';
import { CookieConsentService } from '@shared/services/cookie-consent.service';
import { GlobalBrandingService } from '@shared/services/global-branding.service';
import { MobileAppService } from '@shared/services/mobileApp.service';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, mergeMap, startWith, take } from 'rxjs/operators';

@Component({
  selector: 'cp-root',
  templateUrl: './app.component.html',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly globalBrandingService = inject(GlobalBrandingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appVersionService = inject(AppVersionService);
  private readonly mobileAppService = inject(MobileAppService);
  private readonly cookieConsentService = inject(CookieConsentService);

  readonly appLoading$: Observable<boolean> = combineLatest([
    this.router.events.pipe(
      filter((event) => event instanceof ChildActivationEnd),
      take(1),
      map(() => false),
      startWith(true),
    ),
  ]).pipe(map(([appLoading]) => appLoading));

  readonly mobileAppVersionCompatible$: Observable<boolean> = this.appVersionService.isAppVersionCompatible().pipe(take(1));

  private navigationStartUrl = '';

  constructor() {
    this.cookieConsentService.init();
    this.globalBrandingService.initCompanyColorListener();

    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe((data: { title: string }) => {
        this.titleService.setTitle(`${data.title ? `${data.title} | ` : ''}Mortgage Monitor`);
      });
  }

  ngOnInit(): void {
    // Reset Sign Up CSS styles
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.navigationStartUrl = location.pathname;
      }
      if (
        event instanceof NavigationEnd &&
        !event.url.startsWith(`/${paths.SIGN_UP}`) &&
        this.navigationStartUrl.startsWith(`/${paths.SIGN_UP}`)
      ) {
        window.location.reload();
      }
    });

    // Handle mobile app
    this.mobileAppService.applyAppFeatures();
  }
}
